import React from 'react';
import { formatDate, formatDateMonthYear, formatCurrency, getMonths } from '../../../common/SharedFunctions';
import { getLoanTypeName } from '../LoanFunctions';
import { LoanType } from '../LoanConstants';
import { CommonLoanSummaryAmount } from './CommonLoanSummaryAmount';
import { CommonLoanSummaryInvokingFeatureType } from '../constants/CommonLoanSummaryInvokingFeatureType';

export interface ICommonLoanSummaryParameters {
    loan: any;
    invokingFeature: CommonLoanSummaryInvokingFeatureType;
}

export const CommonLoanSummary = ({ loan, invokingFeature }: ICommonLoanSummaryParameters) => {
    const styles = {
        formColumn: { paddingBottom: '3px' },
        formRow: { marginBottom: '0' },
        sectionHeader: { margin: '10px 0' },
        infoSection: {
            backgroundColor: '#c0c0c07a',
            display: 'inline-block',
            width: '100%',
            padding: '5px',
            marginTop: '5px',
            marginBottom: '10px',
        },
    };

    const formatMultipleAppraisedDates = () => {
        if (loan.allAppraisedDatesSameMonthAndYear) {
            return formatDateMonthYear(loan.allAppraisedDatesSameMonthAndYearDisplay);
        } else {
            return 'Multiple';
        }
    };

    if (!loan) {
        return null;
    }

    return (
        <div style={styles.infoSection}>
            <div
                className='row'
                style={styles.formRow}
            >
                <div
                    className='columns small-12 large-12'
                    style={styles.formColumn}
                >
                    <h2 className='decorated'>{getLoanTypeName(loan.loanTypeId)} Loan Information</h2>
                    {loan.statusComment && (
                        <div
                            style={{
                                fontSize: '14px',
                                marginBottom: '10px',
                                fontStyle: 'italic',
                            }}
                        >
                            <b>{loan.statusComment}</b>
                        </div>
                    )}
                </div>
            </div>
            <div
                className='row'
                style={styles.formRow}
            >
                <div
                    className='columns small-12 large-3'
                    style={styles.formColumn}
                >
                    BFI #: <b>{loan.bfiNumber}</b>
                </div>
                <div
                    className='columns small-12 large-6'
                    style={styles.formColumn}
                >
                    Loan #: <b>{loan.loanNumber}</b>
                </div>
            </div>
            {(loan.borrowerEntity || loan.borrowerContact) && (
                <div
                    className='row'
                    style={styles.formRow}
                >
                    <div
                        className='columns small-12 large-3'
                        style={styles.formColumn}
                    >
                        Borrower:
                    </div>
                    <div
                        className='columns small-12 large-9'
                        style={styles.formColumn}
                    >
                        <b>
                            {loan.borrowerEntity && <span>{loan.borrowerEntity}</span>}
                            {loan.borrowerContact && (
                                <span>
                                    {loan.borrowerEntity && <br />}
                                    {loan.borrowerContact}
                                </span>
                            )}
                        </b>
                    </div>
                </div>
            )}
            {(loan.brokerName || loan.brokerContact) && (
                <div
                    className='row'
                    style={styles.formRow}
                >
                    <div
                        className='columns small-12 large-3'
                        style={styles.formColumn}
                    >
                        Broker:
                    </div>
                    <div
                        className='columns small-12 large-9'
                        style={styles.formColumn}
                    >
                        <b>
                            {loan.brokerName && <span>{loan.brokerName}</span>}
                            {loan.brokerContact && (
                                <span>
                                    {loan.brokerName && <br />}
                                    {loan.brokerContact}
                                </span>
                            )}
                        </b>
                    </div>
                </div>
            )}
            <div
                className='row'
                style={styles.formRow}
            >
                <div
                    className='columns small-12 large-3'
                    style={styles.formColumn}
                >
                    Underwriter:
                </div>
                <div
                    className='columns small-12 large-9'
                    style={styles.formColumn}
                >
                    <b>{loan.underwriter ? loan.underwriter : 'Not Set'}</b>
                </div>
            </div>
            <br />
            <div
                className='row'
                style={styles.formRow}
            >
                <div
                    className='columns small-12 large-3'
                    style={styles.formColumn}
                >
                    Note Date:
                </div>
                <div
                    className='columns small-12 large-9'
                    style={styles.formColumn}
                >
                    <b>{loan.noteDate ? formatDate(loan.noteDate) : 'Not Set'}</b>
                </div>
            </div>
            <div
                className='row'
                style={styles.formRow}
            >
                <div
                    className='columns small-12 large-3'
                    style={styles.formColumn}
                >
                    Orig. Maturity:
                </div>
                <div
                    className='columns small-12 large-9'
                    style={styles.formColumn}
                >
                    <b>{loan.maturityDate ? formatDate(loan.maturityDate) : 'Not Set'}</b>
                    &nbsp;&nbsp;&nbsp;
                    {getMonths(loan.noteDate, loan.maturityDate)}
                </div>
            </div>
            <div
                className='row'
                style={styles.formRow}
            >
                <CommonLoanSummaryAmount
                    loan={loan}
                    invokingFeature={invokingFeature}
                />
            </div>

            {loan.loanTypeId !== LoanType.MasterNote && (
                <>
                    {loan.extensions
                        ?.filter((e) => e.appraisalUpdated)
                        .map((e, index) => (
                            <div
                                key={'ext' + e.id}
                                className='row'
                                style={styles.formRow}
                            >
                                <div
                                    className='columns small-12 large-3'
                                    style={styles.formColumn}
                                >
                                    Extension {index + 1}:
                                </div>
                                <div
                                    className='columns small-12 large-9'
                                    style={styles.formColumn}
                                >
                                    <b>{formatDate(e.extensionDate)}</b>
                                    &nbsp;&nbsp;&nbsp;
                                    {getMonths(
                                        index === 0 ? loan.maturityDate : loan.extensions[index - 1].extensionDate,
                                        e.extensionDate
                                    )}
                                </div>
                            </div>
                        ))}

                    {loan.address && (
                        <>
                            <div
                                className='row'
                                style={styles.formRow}
                            >
                                <div
                                    className='columns small-12 large-12'
                                    style={styles.formColumn}
                                >
                                    <br />
                                    Address:
                                </div>
                            </div>
                            <div
                                className='row'
                                style={styles.formRow}
                            >
                                <div
                                    className='columns small-12 large-12'
                                    style={styles.formColumn}
                                >
                                    {!loan.address && 'Not Set'}
                                    {loan.address && (
                                        <b>
                                            {loan.address.street1}
                                            {loan.address.street2 && <div>{loan.address.street2}</div>}
                                            {(loan.address.city || loan.address.state || loan.address.zipCode) && (
                                                <div>
                                                    {loan.address.city}, {loan.address.state} {loan.address.zipCode}
                                                </div>
                                            )}
                                        </b>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    <div
                        className='row'
                        style={styles.formRow}
                    >
                        <div
                            className='columns small-12 large-12'
                            style={styles.formColumn}
                        >
                            <h2 className='decorated'>Appraisals</h2>
                        </div>
                    </div>
                    <div
                        className='row'
                        style={styles.formRow}
                    >
                        <div
                            className='columns small-12 large-4'
                            style={styles.formColumn}
                        >
                            Value: <b>{loan.appraisedValue ? formatCurrency(loan.appraisedValue, 0) : 'Not Set'}</b>
                        </div>
                        <div
                            className='columns small-12 large-4'
                            style={styles.formColumn}
                        >
                            Date:{' '}
                            <b>
                                {loan.appraisedDate
                                    ? formatDate(loan.appraisedDate)
                                    : loan.hasMultipleAppraisedDates
                                      ? formatMultipleAppraisedDates()
                                      : 'Not Set'}
                            </b>
                        </div>
                        <div
                            className='columns small-12 large-3'
                            style={styles.formColumn}
                        >
                            LTV: <b>{loan.ltv && loan.ltv + '%'}</b>
                        </div>
                    </div>

                    {loan.extensions
                        ?.filter((e) => e.appraisalUpdated)
                        .map((e) => (
                            <div
                                key={'ext' + e.id}
                                className='row'
                                style={styles.formRow}
                            >
                                <div
                                    className='columns small-12 large-4'
                                    style={styles.formColumn}
                                >
                                    Value: <b>{formatCurrency(e.appraisedValue, 0)}</b>
                                </div>
                                <div
                                    className='columns small-12 large-4'
                                    style={styles.formColumn}
                                >
                                    Date: <b>{e.appraisedDate && formatDate(e.appraisedDate)}</b>
                                </div>
                                <div
                                    className='columns small-12 large-3'
                                    style={styles.formColumn}
                                >
                                    LTV: <b>{e.ltv && e.ltv + '%'}</b>
                                </div>
                            </div>
                        ))}
                </>
            )}
        </div>
    );
};
